import { BaseDraft, BaseDraftProps, PaymentRythm } from '@qover/domain';
import {
  addYears,
  differenceInDays,
  isValid,
  subMilliseconds,
  toDate,
} from '@qover/utils/src/date';

import {
  CancellationClaimAmount,
  FamilySituation,
  TripType,
  Variant,
} from '@qover-private/protos-web/qover/dojo/travel/v1/terms';

import { ProductIdentification } from './ProductIdentification';
import { Terms } from './Terms';
import { Risk } from './Risk';
import type { TermsProps } from './Terms';
import type { RiskProps } from './Risk';

export interface DraftProps extends BaseDraftProps {
  terms?: TermsProps;
  risk?: RiskProps;
}

export class Draft extends BaseDraft {
  terms: Terms;
  risk: Risk;

  constructor(draft: DraftProps) {
    super(draft);

    this.terms = new Terms(draft.terms);
    this.risk = new Risk(draft.risk);
  }

  initLocaleAndProduct(locale: string) {
    super.initLocaleAndProduct(locale, 'TRAVEL2');
  }

  getVariant() {
    let td = 0;

    const startDate = new Date(this.startDate ?? '');
    const endDate = this.endDate && new Date(this.endDate);

    if (this.startDate && endDate && isValid(startDate) && isValid(endDate)) {
      td = differenceInDays(startDate, endDate as Date) + 1;
    }

    if (td > 90) {
      return Variant.VARIANT_PREMIUM;
    }

    return Variant.VARIANT_STANDARD;
  }

  getPaymentRythm(paymentRythm?: PaymentRythm) {
    if (paymentRythm) {
      return paymentRythm;
    }

    return this.terms?.isSingleTrip()
      ? PaymentRythm.YEARLY
      : PaymentRythm.MONTHLY;
  }

  getPersonCount() {
    if (this.terms?.isSingleTrip()) {
      return Risk.computePersonsCount(this.risk);
    }

    return 1;
  }

  getTotalPrice(paymentRythm?: PaymentRythm) {
    const rythm = this.getPaymentRythm(paymentRythm);

    const price =
      rythm === PaymentRythm.MONTHLY
        ? this.totalCoverage?.priceInfo?.monthlyPremium
        : this.totalCoverage?.priceInfo?.yearlyPremium;

    return price?.withTaxes ?? 0;
  }

  getTotalBasePrice(paymentRythm?: PaymentRythm) {
    const rythm = this.getPaymentRythm(paymentRythm);

    const price =
      rythm === PaymentRythm.MONTHLY
        ? this.totalCoverage?.basePriceInfo?.monthlyPremium
        : this.totalCoverage?.basePriceInfo?.yearlyPremium;

    return price?.withTaxes ?? 0;
  }

  getAccidentPrice(paymentRythm?: PaymentRythm) {
    const accidentCoverage = this.coverages.getCoverages()?.[
      ProductIdentification.ACCIDENT
    ];
    const rythm = this.getPaymentRythm(paymentRythm);
    const price =
      rythm === PaymentRythm.MONTHLY
        ? accidentCoverage?.priceInfo?.monthlyPremium
        : accidentCoverage?.priceInfo?.yearlyPremium;

    return price?.withTaxes ?? 0;
  }

  getAccidentBasePrice(paymentRythm?: PaymentRythm) {
    const accidentCoverage = this.coverages.getCoverages()?.[
      ProductIdentification.ACCIDENT
    ];
    const rythm = this.getPaymentRythm(paymentRythm);
    const price =
      rythm === PaymentRythm.MONTHLY
        ? accidentCoverage?.basePriceInfo?.monthlyPremium
        : accidentCoverage?.basePriceInfo?.yearlyPremium;

    return price?.withTaxes ?? 0;
  }

  getBaggagePrice(paymentRythm?: PaymentRythm) {
    const baggageCoverage = this.coverages.getCoverages()?.[
      ProductIdentification.BAGGAGE
    ];
    const rythm = this.getPaymentRythm(paymentRythm);
    const price =
      rythm === PaymentRythm.MONTHLY
        ? baggageCoverage?.priceInfo?.monthlyPremium
        : baggageCoverage?.priceInfo?.yearlyPremium;

    return price?.withTaxes ?? 0;
  }

  getBaggageBasePrice(paymentRythm?: PaymentRythm) {
    const baggageCoverage = this.coverages.getCoverages()?.[
      ProductIdentification.BAGGAGE
    ];
    const rythm = this.getPaymentRythm(paymentRythm);
    const price =
      rythm === PaymentRythm.MONTHLY
        ? baggageCoverage?.basePriceInfo?.monthlyPremium
        : baggageCoverage?.basePriceInfo?.yearlyPremium;

    return price?.withTaxes ?? 0;
  }

  getCancellationPrice(paymentRythm?: PaymentRythm) {
    const cancellationClaimAmount = this.terms?.cancellationClaimAmount;

    if (
      !cancellationClaimAmount ||
      cancellationClaimAmount ===
        CancellationClaimAmount.CANCELLATION_CLAIM_AMOUNT_UNSPECIFIED
    ) {
      return 0;
    }

    const accidentCoverage = this.coverages.getCoverages()?.[
      cancellationClaimAmount
    ];
    const rythm = this.getPaymentRythm(paymentRythm);
    const price =
      rythm === PaymentRythm.MONTHLY
        ? accidentCoverage?.priceInfo?.monthlyPremium
        : accidentCoverage?.priceInfo?.yearlyPremium;

    return price?.withTaxes ?? 0;
  }
  getCancellationBasePrice(paymentRythm?: PaymentRythm) {
    const cancellationClaimAmount = this.terms?.cancellationClaimAmount;

    if (
      !cancellationClaimAmount ||
      cancellationClaimAmount ===
        CancellationClaimAmount.CANCELLATION_CLAIM_AMOUNT_UNSPECIFIED
    ) {
      return 0;
    }

    const accidentCoverage = this.coverages.getCoverages()?.[
      cancellationClaimAmount
    ];
    const rythm = this.getPaymentRythm(paymentRythm);
    const price =
      rythm === PaymentRythm.MONTHLY
        ? accidentCoverage?.basePriceInfo?.monthlyPremium
        : accidentCoverage?.basePriceInfo?.yearlyPremium;

    return price?.withTaxes ?? 0;
  }

  getTravelPrice(paymentRythm?: PaymentRythm) {
    const travelCoverage = this.coverages.getCoverages()?.[
      ProductIdentification.TRAVEL
    ];
    const rythm = this.getPaymentRythm(paymentRythm);
    const price =
      rythm === PaymentRythm.MONTHLY
        ? travelCoverage?.priceInfo?.monthlyPremium
        : travelCoverage?.priceInfo?.yearlyPremium;

    return price?.withTaxes ?? 0;
  }

  getTravelBasePrice(paymentRythm?: PaymentRythm) {
    const travelCoverage = this.coverages.getCoverages()?.[
      ProductIdentification.TRAVEL
    ];
    const rythm = this.getPaymentRythm(paymentRythm);
    const price =
      rythm === PaymentRythm.MONTHLY
        ? travelCoverage?.basePriceInfo?.monthlyPremium
        : travelCoverage?.basePriceInfo?.yearlyPremium;

    return price?.withTaxes ?? 0;
  }

  fromSingleTripToYearTrip() {
    this.endDate = undefined;
    this.terms.fromSingleTripToYearTrip();

    return this;
  }

  static getFamilySituation(risk: any) {
    if (risk.adultsList?.length === 1 && risk.childrenList?.length === 0) {
      return FamilySituation.FAMILY_SITUATION_SINGLE;
    }

    if (risk.adultsList?.length === 2 && risk.childrenList?.length === 0) {
      return FamilySituation.FAMILY_SITUATION_COUPLE;
    }

    if (risk.adultsList?.length > 0 && risk.childrenList?.length > 0) {
      return FamilySituation.FAMILY_SITUATION_FAMILY;
    }

    return FamilySituation.FAMILY_SITUATION_UNSPECIFIED;
  }

  static getTripDuration(draft: any) {
    const startDate = toDate(draft?.startDate);
    const endDate = draft?.endDate
      ? toDate(draft?.endDate)
      : subMilliseconds(addYears(new Date(), 1), 1);

    return differenceInDays(endDate, startDate) + 1;
  }

  static isMultiTripPremium(draft: any) {
    return (
      draft?.terms?.tripType === TripType.TRIP_TYPE_MULTI_TRIP &&
      Draft.getTripDuration(draft) > 90
    );
  }

  getDifferenceWithDiscount(paymentRythm?: PaymentRythm) {
    const discountPrice = this.getTotalPrice(paymentRythm);
    const price = this.getTotalBasePrice(paymentRythm);
    return discountPrice - price;
  }

  buildPriceInfoPayload() {
    const defaultPayload = super.buildPriceInfoPayload();

    return {
      ...defaultPayload,
      insuredPersons: Risk.computePersonsCount(this.risk),
      tripType: TripType.TRIP_TYPE_SINGLE_TRIP,
    }
  }
}
